import { useCallback, useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { MENU_ON_SESSION_HEIGHT } from '@/utils/consts'
import { useLocation, useNavigate } from 'react-router-dom'
import { colors } from '@/styles/theme'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import RemixIcon from '@/components/content/RemixIcon'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'
import { FreeMode, Mousewheel } from 'swiper/modules'
import { useStoreSessionDelete } from '@/pages/legacy/hooks/useStoreSessionDelete'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { confirmLeave } from '@/components/layout/leaveGuard'

function MainMenuOnSession() {
  const { list, reload } = useMenuOnSession()
  const { onMenuDelete } = useStoreSessionDelete()
  const [swiper, setSwiper] = useState<SwiperClass>()

  const prevPathname = useRef<string>('')
  const location = useLocation()
  const navigate = useNavigate()
  const onCloseMenuOnSession = useCallback(async (code: string) => {
    // 지금 보는 탭을 닫으면 화면을 떠난다 — 저장 안 한 행이 있으면 먼저 묻는다. 취소하면 탭도 남긴다.
    if (MenuStorage.menuOnSession.getMenuWithCode(code)?.link === window.location.pathname && !(await confirmLeave())) return
    const [removedMenu, removedIdx] = MenuStorage.menuOnSession.remove(code)
    onMenuDelete(code)

    setTimeout(() => {
      const updatedMenuList = MenuStorage.menuOnSession.get()
      reload()
      // 닫는 메뉴가 현재 링크인 경우에 페이지 이동 처리.
      if (removedMenu.link === window.location.pathname) {
        navigate((updatedMenuList[removedIdx] || updatedMenuList[updatedMenuList.length - 1])?.link || '/')
      }
    }, 0)
  }, [])

  const onClickMenu = async (link: string, to: string) => {
    if (link !== location.pathname && !(await confirmLeave())) return
    navigate(to)
  }

  useEffect(() => {
    if (prevPathname.current === location.pathname) {
      return
    }

    const index = list.findIndex((item) => item.link === location.pathname)
    if (index !== -1) {
      prevPathname.current = location.pathname
      swiper?.slideTo(index)
    }
  }, [location.pathname, list, swiper])

  return (
    <MainMenuOnSessionWrapper>
      <Swiper
        freeMode
        slidesPerView="auto"
        autoplay={{ disableOnInteraction: false }}
        modules={[FreeMode, Mousewheel]}
        mousewheel={{
          enabled: true,
          forceToAxis: true,
          invert: false,
          sensitivity: 1,
          releaseOnEdges: false,
        }}
        onSwiper={setSwiper}
      >
        {list.map((menu) => (
          <SwiperSlide key={`moms-menu-on-session-${menu.link}`}>
            <MainMenuOnSessionItem $active={location.pathname.includes(menu.link)} key={`moms-menu-on-session-${menu.link}`}>
              <span
                onClick={() => {
                  onClickMenu(menu.link, `${menu.link}${menu.search ?? ''}`).catch(() => undefined)
                }}
                role="button"
              >
                {menu.name}
              </span>
              <BtnClose>
                <RemixIcon
                  name="ri-close-line"
                  onClick={() => {
                    onCloseMenuOnSession(menu.code).catch(() => undefined)
                  }}
                />
              </BtnClose>
            </MainMenuOnSessionItem>
          </SwiperSlide>
        ))}
      </Swiper>
    </MainMenuOnSessionWrapper>
  )
}

const MainMenuOnSessionWrapper = styled.div`
  display: flex;
  align-items: center;
  background-color: #202020;
  height: ${MENU_ON_SESSION_HEIGHT}px;
  overflow-x: scroll;
  scrollbar-width: none;

  .swiper {
    width: 100%;
    height: 100%;

    .swiper-slide {
      width: 184px;
    }
  }
`

const MainMenuOnSessionItem = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  position: relative;
  padding: 0 24px 0 12px;

  > span {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-grow: 1;
    height: 100%;
    color: ${colors.gray400};
  }

  &:hover > span {
    color: ${colors.white};
  }

  ${({ $active }) =>
    $active &&
    css`
      > span {
        color: ${colors.white};
      }

      &:after {
        content: '';
        width: 100%;
        height: 3px;
        position: absolute;
        left: 0;
        bottom: 0;
        z-index: 1;
        background-color: ${colors.red500};
      }
    `}
`

const BtnClose = styled.span`
  position: absolute;
  right: 2px;
  font-size: 20px;
`

export default MainMenuOnSession
